import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupsIcon from "@mui/icons-material/Groups";
import { Box, Card, Chip, Typography } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { IColorTokens } from "../../../../types/theme";
import { TRIGGER_SOURCE_OPTIONS } from "../data/triggerSources";
import { NotificationTemplate } from "../types/types";

interface NotificationTemplateCardProps {
  template: NotificationTemplate;
  colors: IColorTokens;
  onClick: () => void;
}

export default function NotificationTemplateCard({ template, colors, onClick }: NotificationTemplateCardProps) {
  const triggerLabel =
    TRIGGER_SOURCE_OPTIONS.find((o) => o.value === template.triggerSource)?.label ?? template.triggerSource;

  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: "24px",
        border: `2px solid ${colors.primary[800]}`,
        backgroundColor: colors.primary[900],
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.02)",
        cursor: "pointer",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "scale(1.02)",
          boxShadow: "0px 6px 24px rgba(0, 0, 0, 0.05)",
        },
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem", color: colors.primary[700], mb: 0.5 }}>
        {template.name}
      </Typography>
      <Typography variant="body2" sx={{ color: colors.primary[100], mb: 2 }} noWrap>
        {template.titleTemplate || "No title template set"}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
        <Chip
          label={triggerLabel}
          size="small"
          sx={{ bgcolor: colors.primary[800], color: colors.primary[300], fontWeight: 500, height: 24 }}
        />
        <Chip
          label={template.isActive ? "Active" : "Inactive"}
          size="small"
          sx={{
            bgcolor: template.isActive ? "#EAFCF4" : "#FFF0F0",
            color: template.isActive ? "#25C483" : "#FF6B6B",
            fontWeight: 500,
            height: 24,
          }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: colors.primary[300], pt: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          <GroupsIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" noWrap>
            {template.recipientGroups.length} group{template.recipientGroups.length === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption">
            {formatDistanceToNow(new Date(template.updatedAt), { addSuffix: true })}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}
